
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Contact, DollarSign, Mail } from "lucide-react";
import { LayoutItem } from "../constants";
import { ResizableSwappableCard } from "../ResizableSwappableCard";

interface StatCardProps {
  id: string;
  size: 'small' | 'medium' | 'large' | 'xlarge';
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  handlesVisible: boolean;
  onRemove?: (id: string) => void;
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
}

export const StatCard = ({
  id,
  size,
  title,
  value,
  subtitle,
  icon,
  handlesVisible,
  onRemove,
  onResize
}: StatCardProps) => (
  <ResizableSwappableCard 
    id={id}
    size={size}
    className="h-full" 
    showHandle={handlesVisible}
    showResizeControl={handlesVisible}
    onRemove={handlesVisible ? onRemove : undefined}
    onResize={onResize ? (newSize) => onResize(id, newSize) : undefined}
  >
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">{title}</CardTitle>
      {icon}
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
      <p className="text-xs text-muted-foreground">
        {subtitle}
      </p>
    </CardContent>
  </ResizableSwappableCard>
);

export const TotalUsersCard = ({ 
  item, 
  stats, 
  handlesVisible, 
  onRemove, 
  onResize 
}: { 
  item: LayoutItem; 
  stats: any; 
  handlesVisible: boolean;
  onRemove?: (id: string) => void;
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
}) => (
  <StatCard
    id={item.id}
    size={item.size}
    title="Total Users"
    value={stats.totalUsers}
    subtitle={`+${stats.newUsersWeek} this week`}
    icon={<Users className="h-4 w-4 text-muted-foreground" />}
    handlesVisible={handlesVisible}
    onRemove={onRemove}
    onResize={onResize}
  />
);

export const ContractsCard = ({ 
  item, 
  stats, 
  handlesVisible, 
  onRemove, 
  onResize 
}: { 
  item: LayoutItem; 
  stats: any; 
  handlesVisible: boolean;
  onRemove?: (id: string) => void;
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
}) => (
  <StatCard
    id={item.id}
    size={item.size}
    title="Contracts"
    value={stats.totalContracts}
    subtitle={`By ${stats.userWithMostContracts.count} users`}
    icon={<Contact className="h-4 w-4 text-muted-foreground" />}
    handlesVisible={handlesVisible}
    onRemove={onRemove}
    onResize={onResize}
  />
);

export const TotalValueCard = ({ 
  item, 
  stats, 
  handlesVisible, 
  onRemove, 
  onResize 
}: { 
  item: LayoutItem; 
  stats: any; 
  handlesVisible: boolean;
  onRemove?: (id: string) => void;
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
}) => (
  <StatCard
    id={item.id}
    size={item.size}
    title="Total Value"
    value={`$${stats.totalContractValue.toLocaleString()}`}
    subtitle="Across all contracts"
    icon={<DollarSign className="h-4 w-4 text-muted-foreground" />}
    handlesVisible={handlesVisible}
    onRemove={onRemove}
    onResize={onResize}
  />
);

export const EmailsSentCard = ({ 
  item, 
  stats, 
  handlesVisible, 
  onRemove, 
  onResize 
}: { 
  item: LayoutItem; 
  stats: any; 
  handlesVisible: boolean;
  onRemove?: (id: string) => void;
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
}) => (
  <StatCard
    id={item.id}
    size={item.size}
    title="Emails Sent"
    value={stats.emailsSent}
    subtitle="Non-test reminders"
    icon={<Mail className="h-4 w-4 text-muted-foreground" />}
    handlesVisible={handlesVisible}
    onRemove={onRemove}
    onResize={onResize}
  />
);
